import { useMemo } from "react";
import ProductItem from "./product-item";
import { Badge, Box, Flex, ScrollArea } from "@radix-ui/themes";
import CartIsEmpty from "./cart-is-empty";
import {
  JourneyPredictionCartResponse,
  OrderLine,
  ErrorData,
} from "@/shared/api/lod-api";
import ProductSkeleton from "./product-skeleton";
import { ErrorMessage } from "./error-message";

interface PredictionCartProps {
  predictionCartData: JourneyPredictionCartResponse | undefined;
  isPredictionLoading: boolean;
  predictionError?: ErrorData | null;
}

interface NegativeQuantityWarningProps {
  hasNegativeQuantity: boolean;
}

interface CartContentProps {
  isPredictionLoading: boolean;
  cartItems: OrderLine[];
}

const NegativeQuantityWarning = ({
  hasNegativeQuantity,
}: NegativeQuantityWarningProps) => {
  if (!hasNegativeQuantity) return null;

  return (
    <Box py="2" px="1">
      <Badge color="red" variant="soft">
        Prediction cart item(s) have negative quantities
      </Badge>
    </Box>
  );
};

const CartContent = ({
  isPredictionLoading,
  cartItems,
  hasError,
}: CartContentProps & { hasError: boolean }) => {
  const renderCartItems = () => {
    if (isPredictionLoading) {
      return <ProductSkeleton />;
    }

    if (hasError) {
      return null;
    }

    if (cartItems?.length) {
      return cartItems.map(
        ({ name, image_url, barcode, sku_base_id, quantity, uom }) => (
          <ProductItem
            key={sku_base_id}
            name={name}
            imageUrl={image_url}
            barcode={barcode}
            skuId={sku_base_id}
            quantity={quantity}
            unitOfMeasurement={uom}
            isQuantityNegative={quantity < 0}
          />
        )
      );
    }

    return <CartIsEmpty />;
  };

  return (
    <ScrollArea type="auto" className="h-0 flex-grow">
      <Flex direction="column" gap="2" className="h-0 flex-grow" px="2">
        {renderCartItems()}
      </Flex>
    </ScrollArea>
  );
};

const PredictionCart = ({
  predictionCartData,
  isPredictionLoading,
  predictionError,
}: PredictionCartProps) => {
  const cartItems = useMemo(() => {
    const items = predictionCartData?.result?.lines || [];
    return [...items].sort((a, b) => a.name.localeCompare(b.name));
  }, [predictionCartData]);

  const hasNegativeQuantity = useMemo(() => {
    return cartItems.some((item) => item.quantity < 0);
  }, [cartItems]);

  const hasError = !!predictionError;

  return (
    <Flex direction="column" gap="2" className="h-0 flex-grow">
      <ErrorMessage error={predictionError} />
      {!hasError && (
        <NegativeQuantityWarning hasNegativeQuantity={hasNegativeQuantity} />
      )}
      <CartContent
        isPredictionLoading={isPredictionLoading}
        cartItems={cartItems}
        hasError={hasError}
      />
    </Flex>
  );
};

export default PredictionCart;
